/**
 * @fileoverview A single day/time cell and its appointments in CustomCalendar.
 * @module components/CustomCalendar/CustomCalendarSlot
 */
import styles from "./CustomCalendar.module.css";
import {
  getAppointmentTimingStatus,
  getAppointmentType,
  getAppointmentsForDateTime,
  hasCompanyUserConflict,
} from "../../utils/calendar/calendarAppointmentUtils.js";
import {
  hasBusinessEventOnDate,
  isTimeSlotInPast,
  isTimeSlotOverlappingBreak,
} from "../../utils/calendar/calendarTimeUtils.js";
import {
  formatAppointmentEventTime,
  getBreakInfo,
  getDaySlotClassName,
  isAppointmentDraggable,
  isCurrentAppointment,
  isCurrentTime,
  isDragOverTimeSlot,
  isFirstBreakSlot,
  isSelectedTimeSlot,
  isSenderAppointment,
  isValidDropTarget,
  isValidTimeSlot,
} from "./customCalendarUtils.js";

const CustomCalendarAppointment = ({
  appointment,
  senderAppointments,
  currentAppointment,
  highlightedRequirementId,
  disableDragAndDrop,
  allowDragAllAppointments,
  onDragStart,
  onDragEnd,
  t,
}) => {
  const appointmentType = getAppointmentType(appointment);
  const timingStatus = getAppointmentTimingStatus(appointment);
  const isSender = isSenderAppointment(appointment, senderAppointments);
  const isCurrent = isCurrentAppointment(appointment, currentAppointment);
  const isHighlightedRequirement =
    highlightedRequirementId != null &&
    String(appointment.idRequerimiento) === String(highlightedRequirementId);
  const draggable = isAppointmentDraggable(appointment, {
    disableDragAndDrop,
    allowDragAllAppointments,
    currentAppointment,
  });

  return (
    <div
      className={`${styles.event} ${
        styles[`appointment-${appointmentType}`] || ""
      } ${styles[appointment.estado?.toLowerCase()] || ""} ${
        styles[`timing-${timingStatus}`]
      } ${isSender ? styles.senderAppointment : ""} ${
        isCurrent ? styles.currentAppointment : ""
      } ${isHighlightedRequirement ? styles.requirementHighlighted : ""} ${
        !draggable ? styles.nonDraggable : ""
      }`}
      draggable={draggable}
      onDragStart={(e) => onDragStart(e, appointment)}
      onDragEnd={onDragEnd}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className={styles.eventTitle}>
        {isCurrent
          ? t("pages.viewAppointments.selectedAppointment")
          : appointment.memberName || t("pages.viewAppointments.appointment")}
      </div>
      <div className={styles.eventTime}>
        {formatAppointmentEventTime(appointment)}
      </div>
    </div>
  );
};

const CustomCalendarSlot = ({
  date,
  slot,
  slotIndex,
  timeSlots,
  cellsPerHour,
  t,
  appointments,
  senderAppointments,
  currentAppointment,
  highlightedRequirementId,
  selectedDate,
  selectedTime,
  selectedMinute,
  draggedAppointment,
  dragOverTimeSlot,
  disableDragAndDrop,
  allowDragAllAppointments,
  breakTimes,
  eventData,
  allAppointments,
  onDragOver,
  onDragLeave,
  onDrop,
  onDateSelect,
  onDragStart,
  onDragEnd,
}) => {
  const dayAppointments = getAppointmentsForDateTime(appointments, date, slot);
  const isBusinessEventDay = hasBusinessEventOnDate(date, eventData);
  const currentTimePosition = isCurrentTime(slot);
  const isPast = isTimeSlotInPast(date, slot);
  const isValidDrop = isValidDropTarget({
    date,
    timeSlot: slot,
    breakTimes,
    eventData,
    appointments,
  });
  const isValidSlot = isValidTimeSlot({
    date,
    timeSlot: slot,
    breakTimes,
    eventData,
    appointments,
    allAppointments,
    draggedAppointment,
    currentAppointment,
  });
  const hasConflict = hasCompanyUserConflict({
    date,
    timeSlot: slot,
    appointmentToCheck: draggedAppointment || currentAppointment,
    allAppointments,
    appointments,
  });
  const isInBreak =
    isBusinessEventDay && isTimeSlotOverlappingBreak(slot, breakTimes);
  const breakInfo = getBreakInfo(breakTimes);
  const firstBreakSlot = isFirstBreakSlot(
    isInBreak,
    slotIndex,
    timeSlots,
    breakTimes,
  );
  const isSelected = isSelectedTimeSlot(
    selectedDate,
    selectedTime,
    selectedMinute,
    date,
    slot,
  );
  const isCurrentHour = new Date().getHours() === slot.hour;
  const isDragOver = isDragOverTimeSlot(dragOverTimeSlot, date, slot);

  return (
    <div
      className={getDaySlotClassName(styles, {
        isBusinessEventDay,
        isCurrentHour,
        isDragOver,
        isValidDrop,
        isSelected,
        isPast,
        draggedAppointment,
        hasConflict,
        currentAppointment,
        isValidSlot,
        slot,
        isInBreak,
      })}
      data-current-minute={
        currentTimePosition !== false ? currentTimePosition : null
      }
      style={{
        height: `${60 / cellsPerHour}px`,
      }}
      onDragOver={(e) => onDragOver(e, date, slot)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, date, slot)}
      onClick={() => {
        if (isValidSlot && onDateSelect) {
          onDateSelect(date, slot.hour, slot.minute);
        }
      }}
    >
      {currentTimePosition !== false && (
        <div
          className={styles.currentTimeLine}
          style={{
            top: `calc(${currentTimePosition} * 0.6px)`,
          }}
        />
      )}
      {isBusinessEventDay && isInBreak && firstBreakSlot && breakInfo && (
        <div className={styles.breakEvent}>
          <div className={styles.breakTitle}>
            {t("pages.viewAppointments.lunchBreak")}
          </div>
          <div className={styles.breakTime}>
            {breakInfo.startTime} - {breakInfo.endTime}
          </div>
        </div>
      )}
      {dayAppointments.map((appointment, appointmentIndex) => (
        <CustomCalendarAppointment
          key={appointmentIndex}
          appointment={appointment}
          senderAppointments={senderAppointments}
          currentAppointment={currentAppointment}
          highlightedRequirementId={highlightedRequirementId}
          disableDragAndDrop={disableDragAndDrop}
          allowDragAllAppointments={allowDragAllAppointments}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          t={t}
        />
      ))}
    </div>
  );
};

export default CustomCalendarSlot;
